import React from 'react';
import { Box, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { LogStatusFilter, ProcessStatus } from '../types';
import { PROCESS_STATUS_VISUALS } from './SystemAccessLogStatusPill';

interface FilterOption {
  value: LogStatusFilter;
  label: string;
  color?: string;
}

const PROCESS_STATUS_ORDER: ProcessStatus[] = ['applied', 'planned', 'not_sent', 'cancelled'];

const FILTER_OPTIONS: FilterOption[] = [
  { value: 'all', label: 'Alle' },
  ...PROCESS_STATUS_ORDER.map((status) => ({
    value: status as LogStatusFilter,
    label: PROCESS_STATUS_VISUALS[status].label,
    color: PROCESS_STATUS_VISUALS[status].color,
  })),
];

interface Props {
  value: LogStatusFilter;
  counts: Record<LogStatusFilter, number>;
  onChange: (next: LogStatusFilter) => void;
}

function SystemAccessLogFilterBar({ value, counts, onChange }: Props) {
  const theme = useTheme();

  return (
    <Box
      role="tablist"
      aria-label="Aktionsprotokoll filtern"
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 1,
        mb: 2,
      }}
    >
      {FILTER_OPTIONS.map((option) => {
        const isActive = value === option.value;
        const accent = option.color || theme.palette.text.primary;
        const count = counts[option.value] ?? 0;
        return (
          <Box
            key={option.value}
            role="tab"
            aria-selected={isActive}
            data-filter={option.value}
            tabIndex={0}
            onClick={() => onChange(option.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onChange(option.value);
              }
            }}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1.5,
              py: 0.65,
              borderRadius: 999,
              cursor: 'pointer',
              userSelect: 'none',
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: 0.2,
              color: isActive ? accent : theme.palette.text.secondary,
              bgcolor: isActive ? alpha(accent, 0.16) : alpha(theme.palette.text.primary, 0.04),
              border: `1px solid ${isActive ? alpha(accent, 0.5) : alpha(theme.palette.divider, 0.7)}`,
              transition: 'background-color 120ms ease, color 120ms ease, border-color 120ms ease',
              '&:hover': {
                bgcolor: isActive ? alpha(accent, 0.22) : alpha(accent, 0.08),
                color: accent,
                borderColor: alpha(accent, 0.4),
              },
              '&:focus-visible': {
                outline: `2px solid ${alpha(accent, 0.6)}`,
                outlineOffset: 2,
              },
            }}
          >
            <Box component="span">{option.label}</Box>
            <Box
              component="span"
              sx={{
                fontSize: 11,
                fontWeight: 700,
                px: 0.75,
                py: 0.05,
                borderRadius: 999,
                bgcolor: alpha(accent, isActive ? 0.22 : 0.12),
                color: isActive ? accent : theme.palette.text.secondary,
                minWidth: 22,
                textAlign: 'center',
              }}
            >
              {count}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

export default SystemAccessLogFilterBar;
